/**
 * VoiceSpatialRouter — route each remote peer's audio through a PannerNode
 * so it spatializes against the game's `AudioListener`.
 *
 * Chrome / Safari quirk: `MediaStreamAudioSourceNode` may receive no
 * samples unless the stream is ALSO attached to a muted <audio> element
 * — a long-standing bug documented on three.js forums and the Chromium
 * tracker. We do both.
 *
 * Panner config: HRTF for directional cues, inverse distance with
 * configurable `maxDistance` and `rolloffFactor`.
 */

export interface SpatialVoiceConfig {
  refDistance?: number;
  maxDistance?: number;
  rolloffFactor?: number;
}

interface RoutedPeer {
  peerId: string;
  source: MediaStreamAudioSourceNode;
  panner: PannerNode;
  /** Hidden audio element — the "Chrome tax" for MediaStream pipelines. */
  element: HTMLAudioElement | null;
  /** Current world position. */
  x: number; y: number; z: number;
}

export class VoiceSpatialRouter {
  readonly ctx: AudioContext;
  /** Output gain — caller connects this to the voice bus. */
  readonly output: GainNode;
  readonly config: Required<SpatialVoiceConfig>;

  private _peers = new Map<string, RoutedPeer>();

  constructor(ctx: AudioContext, parentBus?: AudioNode, config: SpatialVoiceConfig = {}) {
    this.ctx = ctx;
    this.output = ctx.createGain();
    this.config = {
      refDistance: config.refDistance ?? 1,
      maxDistance: config.maxDistance ?? 50,
      rolloffFactor: config.rolloffFactor ?? 1,
    };
    if (parentBus) this.output.connect(parentBus);
    else this.output.connect(ctx.destination);
  }

  /**
   * Route a remote peer's stream through the spatial graph. The caller
   * gets back the PannerNode so they can swap panning models or mutate
   * more params if needed.
   */
  attach(peerId: string, stream: MediaStream): PannerNode {
    if (this._peers.has(peerId)) this.detach(peerId);

    // The "Chrome tax": attach the stream to a muted, autoplaying <audio>
    // element or the MediaStreamAudioSourceNode will starve.
    let element: HTMLAudioElement | null = null;
    if (typeof document !== 'undefined') {
      element = document.createElement('audio');
      element.srcObject = stream;
      element.muted = true;
      element.autoplay = true;
    }

    const source = this.ctx.createMediaStreamSource(stream);
    const panner = this.ctx.createPanner();
    panner.panningModel = 'HRTF';
    panner.distanceModel = 'inverse';
    panner.refDistance = this.config.refDistance;
    panner.maxDistance = this.config.maxDistance;
    panner.rolloffFactor = this.config.rolloffFactor;
    source.connect(panner).connect(this.output);

    this._peers.set(peerId, {
      peerId, source, panner, element,
      x: 0, y: 0, z: 0,
    });
    return panner;
  }

  /** Update a peer's world position — caller calls this per frame from ECS. */
  setPosition(peerId: string, x: number, y: number, z: number): void {
    const p = this._peers.get(peerId);
    if (!p) return;
    p.x = x; p.y = y; p.z = z;
    // Position the panner.
    if (p.panner.positionX) {
      const t = this.ctx.currentTime;
      p.panner.positionX.setValueAtTime(x, t);
      p.panner.positionY.setValueAtTime(y, t);
      p.panner.positionZ.setValueAtTime(z, t);
    } else {
      // Legacy Safari path.
      (p.panner as unknown as {
        setPosition: (x: number, y: number, z: number) => void;
      }).setPosition(x, y, z);
    }
  }

  /** Disconnect and clean up a peer. */
  detach(peerId: string): void {
    const p = this._peers.get(peerId);
    if (!p) return;
    try { p.source.disconnect(); } catch { /* ignore */ }
    try { p.panner.disconnect(); } catch { /* ignore */ }
    if (p.element) {
      try { p.element.pause(); p.element.srcObject = null; } catch { /* ignore */ }
    }
    this._peers.delete(peerId);
  }

  /** All currently-routed peer ids (diagnostic). */
  get peerIds(): string[] { return Array.from(this._peers.keys()); }

  /** Dispose all peers + output. */
  dispose(): void {
    for (const id of Array.from(this._peers.keys())) this.detach(id);
    try { this.output.disconnect(); } catch { /* ignore */ }
  }
}
